// Centralized API client for frontend
import axios from 'axios';
import { attachBackendAuth } from './backendAuth';

// Environment-aware API configuration
const resolveApiBase = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed && trimmed !== "undefined" && trimmed !== "null") {
        return trimmed.replace(/\/$/, "");
      }
    }
  }
  return "";
};

const DEFAULT_PRODUCTION_API = "https://optra-backend.onrender.com";

const isTruthyEnv = (v) =>
  ["1", "true", "yes"].includes(String(v || "").toLowerCase());

const isLocalhostApiUrl = (url) =>
  typeof url === "string" &&
  /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(url.trim());

/** True when the page is served on loopback (dev, vite preview, or static server on :5173/:4173). */
function isBrowserLoopbackHostname() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "[::1]"
  );
}

/**
 * Match services/api.js: in dev, prefer same-origin requests so Vite can proxy /api
 * (avoids browser CORS and direct calls to a dead localhost:8000).
 */
const getApiBaseUrl = () => {
  const raw = resolveApiBase(import.meta?.env?.VITE_API_BASE_URL);
  const trimmed = raw || "";
  const useSameOriginProd = isTruthyEnv(import.meta?.env?.VITE_USE_SAME_ORIGIN_API);
  const forceDirectApi = isTruthyEnv(import.meta?.env?.VITE_FORCE_DIRECT_API);

  try {
    if (import.meta?.env?.DEV) {
      const useLocal = ["1", "true", "yes"].includes(
        String(import.meta.env.VITE_USE_LOCAL_BACKEND || "").toLowerCase()
      );
      if (useLocal) {
        if (import.meta?.env?.VITE_SUPPRESS_API_WARNINGS !== "true") {
          console.info(
            "🌐 Ask OPTRA API (dev): same-origin /api via Vite →",
            trimmed || "http://127.0.0.1:8000 (set VITE_API_BASE_URL to override)"
          );
        }
        return "";
      }
      if (!trimmed) {
        return "";
      }
      if (trimmed === DEFAULT_PRODUCTION_API.replace(/\/$/, "")) {
        return "";
      }
      if (isLocalhostApiUrl(trimmed)) {
        if (import.meta?.env?.VITE_SUPPRESS_API_WARNINGS !== "true") {
          console.info(
            "🌐 Ask OPTRA API (dev): same-origin /api via Vite →",
            trimmed
          );
        }
        return "";
      }
      if (import.meta?.env?.VITE_SUPPRESS_API_WARNINGS !== "true") {
        console.info("🌐 Ask OPTRA API (dev): direct base URL:", trimmed);
      }
      return trimmed;
    }
  } catch {
    /* import.meta unavailable */
  }

  // Production build opened on localhost / 127.0.0.1: use relative /api so Vite `preview` (or any
  // loopback server) can proxy to Render or local FastAPI — same as dev, no CORS.
  // Opt out: VITE_FORCE_DIRECT_API=true to always call VITE_API_BASE_URL or the default Render host.
  if (
    !import.meta.env.DEV &&
    isBrowserLoopbackHostname() &&
    !forceDirectApi
  ) {
    return "";
  }

  // Production / preview: same-origin /api when hosting rewrites /api → Render (see vercel.json).
  if (useSameOriginProd) {
    return "";
  }

  const production = trimmed || DEFAULT_PRODUCTION_API;
  if (import.meta?.env?.DEV && import.meta?.env?.VITE_SUPPRESS_API_WARNINGS !== "true") {
    console.info("🌐 API Base URL:", production);
    if (!trimmed) {
      console.warn(
        "⚠️ VITE_API_BASE_URL not set — using default production host for non-DEV or fallback."
      );
    }
  }

  return production;
};

const baseURL = getApiBaseUrl();
export const API_BASE = baseURL;  // Named export for absolute URL construction

// Log API configuration once on load (dev / local preview / Vercel previews)
if (
  (import.meta.env.DEV ||
    window.location.hostname.includes("vercel.app") ||
    isBrowserLoopbackHostname()) &&
  import.meta?.env?.VITE_SUPPRESS_API_WARNINGS !== "true"
) {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🌐 Ask OPTRA API Configuration');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('Base URL:', baseURL);
  console.log('Timeout:', '90 seconds');
  console.log('Demo Mode:', import.meta.env.VITE_DUMMY_MODE === 'true' ? 'ENABLED' : 'DISABLED');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
}

// Generate trace ID for debugging
function generateTraceId() {
  return `trace-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export const api = axios.create({
  baseURL,
  timeout: 90000, // 90s for LLM responses - matches backend timeout
  withCredentials: false, // set true only if using cookie auth
  headers: { 
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    // Origin header is set automatically by browser - don't set manually to avoid CORS warnings
  },
});

attachBackendAuth(api);

// Add request interceptor for trace IDs and debugging
api.interceptors.request.use(
  (config) => {
    // Add trace ID to every request
    const traceId = generateTraceId();
    config.headers['X-Trace-Id'] = traceId;
    
    const fullUrl = config.baseURL + config.url;
    console.debug(`→ [${traceId}] ${config.method?.toUpperCase()} ${fullUrl}`, { headers: config.headers, data: config.data });
    return config;
  },
  (error) => {
    console.error('Request setup failed:', error.name, error.message);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    const traceId = response.config.headers?.['X-Trace-Id'] || 'no-trace';
    console.debug(`← [${traceId}] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    const traceId = error.config?.headers?.["X-Trace-Id"] || "no-trace";
    const reqUrl = error.config?.url || "";
    const errData = error.response?.data;
    const errDetail = {
      name: error.name,
      message: error.message,
      status: error.response?.status,
      url: reqUrl,
      data: errData,
      traceId: traceId,
    };

    if (error.response) {
      console.error(`[${traceId}] API request failed:`, errDetail);
      if (errData) {
        try {
          console.error(
            `[${traceId}] API error body (JSON):`,
            JSON.stringify(errData, null, 2)
          );
        } catch {
          console.error(`[${traceId}] API error body (raw):`, errData);
        }
      }
    } else if (import.meta.env.DEV) {
      console.warn(`[${traceId}] API network error (no response).`, errDetail);
    } else {
      console.error(`[${traceId}] API request failed:`, errDetail);
      console.error(`[${traceId}] Network/CORS error (no response):`, error.toJSON?.() || error);
    }

    return Promise.reject(error);
  }
);

// Health check endpoint
export async function ping() {
  return api.get('/health');
}

// FAQ Feedback API
export async function sendFaqFeedback(payload) {
  // Support both object and positional arguments for backwards compatibility
  const feedbackData = typeof payload === 'object' && payload.id
    ? payload
    : {
        id: arguments[0],
        vote: arguments[1],
        comment: arguments[2] || null,
        user_id: arguments[3] || null
      };
  
  console.log('[sendFaqFeedback] Submitting to backend:', feedbackData);
  
  const res = await api.post('/api/faq/feedback', feedbackData);
  return res.data;
}

// Eligibility API
export async function checkEligibility(data) {
  const res = await api.post('/eligibility/run', data);
  return res.data;
}

export default api;
